/**
 * <mrk-player>: the global sticky beat player (snippets/mrk-player.liquid).
 * One <audio preload="none">; WaveSurfer 8 is lazy-loaded on the first play and drives the waveform from that same
 * element, so playback starts at once while the waveform decodes. Contract: .claude/docs/design.md → Player contract.
 */
import { MRK_EVENTS, emit, on, beatFromElement, queueFromElement } from './mrk-events.js';

const STORAGE_KEY = 'mrk:player';
const PHONE = '(max-width: 809px)';
const STATE_INTERVAL = 250;
const SAVE_INTERVAL = 2000;
const SEEK_STEP = 5;
const RESTART_THRESHOLD = 3;

/** @param {number} seconds */
const formatTime = (seconds) => {
  const s = Number.isFinite(seconds) && seconds > 0 ? Math.floor(seconds) : 0;
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

/** Elements that use Space natively (activation). */
const SPACE_OWNERS =
  'input, textarea, select, button, a[href], summary, [contenteditable=""], [contenteditable="true"], [role="slider"], [role="listbox"], [tabindex]:not([tabindex="-1"])';
/** Elements that use the arrow keys natively. */
const ARROW_OWNERS =
  'input, textarea, select, [contenteditable=""], [contenteditable="true"], [role="slider"], [role="listbox"]';

/** @typedef {import('./mrk-events.js').MrkBeat} MrkBeat */

class MrkPlayer extends HTMLElement {
  /** @type {MrkBeat[]} */
  queue = [];
  index = -1;
  shuffle = false;
  repeat = false;
  /** @type {number | null} */
  #pendingTime = null;
  #savedDuration = 0;
  #lastState = 0;
  #lastSave = 0;
  #resume = false;
  /** @type {Promise<any> | null} */
  #wavePromise = null;
  #waveSrc = '';
  #syncQueued = false;
  #strings = /** @type {Record<string, string>} */ ({});
  #phone = window.matchMedia(PHONE);

  connectedCallback() {
    this.audio = /** @type {HTMLAudioElement} */ (this.querySelector('[data-mrk-audio]'));
    this.sheet = /** @type {HTMLDialogElement} */ (this.querySelector('[data-mrk-sheet]'));
    this.wave = /** @type {HTMLElement} */ (this.querySelector('[data-mrk-wave]'));
    this.seekInput = /** @type {HTMLInputElement} */ (this.querySelector('[data-mrk-seek]'));
    this.volumeInput = /** @type {HTMLInputElement} */ (this.querySelector('[data-mrk-volume]'));
    this.queuePanel = /** @type {HTMLElement} */ (this.querySelector('[data-mrk-queue-panel]'));
    try {
      this.#strings = JSON.parse(this.querySelector('[data-mrk-strings]')?.textContent || '{}');
    } catch {
      this.#strings = {};
    }

    this.#bindAudio();
    this.addEventListener('click', this.#onClick);
    this.seekInput.addEventListener('input', () => {
      const duration = this.#duration();
      if (duration) this.#seekTo((Number(this.seekInput.value) / 1000) * duration);
    });
    this.volumeInput.addEventListener('input', () => {
      this.audio.muted = false;
      this.audio.volume = Number(this.volumeInput.value) / 100;
    });
    this.sheet.addEventListener('close', () => this.#onSheetClosed());
    this.sheet.addEventListener('click', (event) => {
      if (event.target === this.sheet) this.sheet.close();
    });
    this.#bindSheetDrag();
    this.#phone.addEventListener('change', () => {
      if (!this.#phone.matches && this.sheet.open) this.sheet.close();
    });

    document.addEventListener('click', this.#onDocumentClick);
    document.addEventListener('keydown', this.#onKeydown);
    window.addEventListener('pagehide', () => this.#save(true));
    on(MRK_EVENTS.beatPlay, (event) => {
      const beat = event.detail;
      if (beat) this.play(beat, beat.queue);
    });
    on(MRK_EVENTS.beatToggle, (event) => this.#toggleById(event.detail?.id));
    on(MRK_EVENTS.queueSet, (event) => this.setQueue(event.detail?.items || []));

    new MutationObserver((records) => {
      if (this.index < 0) return;
      for (const record of records) {
        for (const node of record.addedNodes) {
          if (node instanceof Element && (node.matches('[data-mrk-play]') || node.querySelector('[data-mrk-play]'))) {
            this.#queueSync();
            return;
          }
        }
      }
    }).observe(document.body, { childList: true, subtree: true });

    this.#setupMediaSession();
    this.#restore();
  }

  /** @returns {MrkBeat | undefined} */
  get current() {
    return this.queue[this.index];
  }

  get playing() {
    return !this.audio.paused && !this.audio.ended;
  }

  /**
   * Plays a beat, optionally replacing the queue. Sold beats and beats without audio are ignored.
   * @param {MrkBeat} beat
   * @param {MrkBeat[]} [queue]
   */
  play(beat, queue) {
    if (!beat || beat.sold || !beat.audio) return;
    const items = (Array.isArray(queue) && queue.length ? queue : [beat]).filter((item) => item && !item.sold && item.audio);
    let index = items.findIndex((item) => String(item.id) === String(beat.id));
    if (index < 0) {
      items.unshift(beat);
      index = 0;
    }
    this.queue = items;
    this.#load(index, { autoplay: true });
    this.#renderQueue();
  }

  /** @param {MrkBeat[]} items */
  setQueue(items) {
    const playable = items.filter((item) => item && !item.sold && item.audio);
    const current = this.current;
    if (!current) {
      this.queue = playable;
      this.index = -1;
      return;
    }
    const index = playable.findIndex((item) => String(item.id) === String(current.id));
    this.queue = index < 0 ? [current, ...playable] : playable;
    this.index = Math.max(index, 0);
    this.#renderQueue();
    this.#save(true);
  }

  toggle() {
    if (!this.current) return;
    if (this.playing) this.audio.pause();
    else this.#playAudio();
  }

  next(auto = false) {
    if (!this.queue.length) return;
    let index = this.index + 1;
    if (this.shuffle && this.queue.length > 1) {
      do index = Math.floor(Math.random() * this.queue.length);
      while (index === this.index);
    } else if (index >= this.queue.length) {
      if (auto && !this.repeat) return;
      index = 0;
    }
    this.#load(index, { autoplay: true });
  }

  prev() {
    if (!this.current) return;
    if (this.audio.currentTime > RESTART_THRESHOLD || this.index === 0) {
      this.#seekTo(0);
      if (!this.playing) this.#playAudio();
      return;
    }
    this.#load(this.index - 1, { autoplay: true });
  }

  close() {
    this.audio.pause();
    if (this.sheet.open) this.sheet.close();
    this.#toggleQueue(false);
    this.hidden = true;
    document.body.classList.remove('mrk--player-open');
    this.index = -1;
    this.#resume = false;
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {}
    this.#syncButtons();
    emit(MRK_EVENTS.playerState, { id: null, playing: false, currentTime: 0, duration: 0 });
  }

  /**
   * @param {number} index
   * @param {{ autoplay?: boolean, time?: number }} [options]
   */
  #load(index, { autoplay = false, time = 0 } = {}) {
    const beat = this.queue[index];
    if (!beat) return;
    this.index = index;
    const src = new URL(beat.audio, location.href).href;
    if (this.audio.src !== src) {
      this.#savedDuration = 0;
      this.audio.src = src;
      this.#pendingTime = time || null;
    } else {
      this.#seekTo(time);
    }
    this.#renderTrack();
    this.#show();
    if (autoplay) this.#playAudio();
    this.#update(true);
    this.#save(true);
  }

  #playAudio() {
    this.#resume = false;
    this.classList.remove('mrk-player--resume');
    this.audio.play().catch(() => this.#update(true));
    if (this.current) this.#loadWave(this.audio.src, this.current.id, this.current.wave);
  }

  /** @param {number} time */
  #seekTo(time) {
    if (this.audio.readyState > 0) this.audio.currentTime = Math.max(0, time);
    else this.#pendingTime = time;
    this.#update(true);
  }

  #duration() {
    const duration = this.audio.duration;
    return Number.isFinite(duration) && duration > 0 ? duration : this.#savedDuration;
  }

  /** @param {number | string | undefined} id */
  #toggleById(id) {
    if (id == null) return;
    if (this.current && String(this.current.id) === String(id)) return this.toggle();
    const button = document.querySelector(`[data-mrk-play][data-mrk-beat-id="${CSS.escape(String(id))}"]`);
    const beat = this.queue.find((item) => String(item.id) === String(id)) || beatFromElement(button);
    if (beat) this.play(beat, button ? queueFromElement(button) : this.queue);
  }

  #show() {
    if (!this.hidden) return;
    this.hidden = false;
    document.body.classList.add('mrk--player-open');
  }

  #bindAudio() {
    const audio = this.audio;
    audio.addEventListener('loadedmetadata', () => {
      if (this.#pendingTime) audio.currentTime = Math.min(this.#pendingTime, audio.duration || this.#pendingTime);
      this.#pendingTime = null;
      this.#update(true);
    });
    audio.addEventListener('play', () => this.#update(true));
    audio.addEventListener('pause', () => {
      this.#update(true);
      this.#save(true);
    });
    audio.addEventListener('timeupdate', () => this.#update());
    audio.addEventListener('ended', () => {
      if (this.index < this.queue.length - 1 || this.repeat || this.shuffle) this.next(true);
      else this.#update(true);
    });
    audio.addEventListener('volumechange', () => {
      this.toggleAttribute('data-mrk-muted', audio.muted || audio.volume === 0);
      this.volumeInput.value = String(Math.round((audio.muted ? 0 : audio.volume) * 100));
      const mute = this.querySelector('[data-mrk-action="mute"]');
      mute?.setAttribute('aria-label', (audio.muted ? this.#strings.unmute : this.#strings.mute) || '');
      this.#save(true);
    });
  }

  /** Updates time, progress, button states, Media Session and (throttled) emits mrk:player:state. */
  #update(force = false) {
    const now = performance.now();
    const currentTime = this.#pendingTime ?? this.audio.currentTime;
    const duration = this.#duration();
    const ratio = duration ? Math.min(currentTime / duration, 1) : 0;

    this.style.setProperty('--mrk-progress', String(ratio));
    for (const el of this.querySelectorAll('[data-mrk-time="current"]')) el.textContent = formatTime(currentTime);
    for (const el of this.querySelectorAll('[data-mrk-time="duration"]')) el.textContent = formatTime(duration);
    if (document.activeElement !== this.seekInput) this.seekInput.value = String(Math.round(ratio * 1000));
    this.seekInput.setAttribute(
      'aria-valuetext',
      (this.#strings.seekValue || '')
        .replace('__mrk_current__', formatTime(currentTime))
        .replace('__mrk_duration__', formatTime(duration))
    );

    if (!force && now - this.#lastState < STATE_INTERVAL) return;
    this.#lastState = now;

    const playing = this.playing;
    const title = this.current?.title || '';
    const label = this.#label(playing ? 'pause' : this.#resume ? 'resume' : 'play', title);
    this.classList.toggle('mrk-player--playing', playing);
    for (const button of this.querySelectorAll('[data-mrk-action="toggle"]')) {
      button.setAttribute('aria-pressed', String(playing));
      button.setAttribute('aria-label', label);
    }
    this.#syncButtons();

    if ('mediaSession' in navigator) {
      navigator.mediaSession.playbackState = playing ? 'playing' : 'paused';
      if (duration && this.audio.readyState > 0) {
        try {
          navigator.mediaSession.setPositionState({
            duration,
            position: Math.min(this.audio.currentTime, duration),
            playbackRate: this.audio.playbackRate || 1,
          });
        } catch {}
      }
    }

    emit(MRK_EVENTS.playerState, { id: this.current?.id ?? null, playing, currentTime, duration });
    if (now - this.#lastSave > SAVE_INTERVAL) this.#save();
  }

  /**
   * @param {string} key
   * @param {string} title
   */
  #label(key, title) {
    return (this.#strings[key] || '').replace('__mrk_title__', title);
  }

  /** Keeps every [data-mrk-play] button on the page in sync: aria-pressed + its play/pause aria-label. */
  #syncButtons() {
    const id = this.current ? String(this.current.id) : '';
    const playing = this.playing;
    for (const button of document.querySelectorAll('[data-mrk-play]')) {
      if (!(button instanceof HTMLElement)) continue;
      const pressed = playing && button.dataset.mrkBeatId === id;
      const isCurrent = !!id && button.dataset.mrkBeatId === id;
      button.toggleAttribute('data-mrk-current', isCurrent);
      if (button.getAttribute('aria-pressed') === String(pressed)) continue;
      button.setAttribute('aria-pressed', String(pressed));
      const label = pressed ? button.dataset.mrkLabelPause : button.dataset.mrkLabelPlay;
      if (label) button.setAttribute('aria-label', label);
    }
  }

  #queueSync() {
    if (this.#syncQueued) return;
    this.#syncQueued = true;
    requestAnimationFrame(() => {
      this.#syncQueued = false;
      this.#syncButtons();
    });
  }

  #renderTrack() {
    const beat = this.current;
    if (!beat) return;
    const bpm = beat.bpm ? (this.#strings.bpm || '').replace('__mrk_bpm__', beat.bpm) : '';
    const meta = [bpm, beat.key, beat.genre].filter(Boolean).join(' · ');
    const buyLabel = this.#label('buy', beat.title);

    for (const el of this.querySelectorAll('[data-mrk-field]')) {
      switch (/** @type {HTMLElement} */ (el).dataset.mrkField) {
        case 'title':
          el.textContent = beat.title;
          break;
        case 'meta':
          el.textContent = meta;
          break;
        case 'price':
          el.textContent = beat.priceFrom || this.#strings.buyText || '';
          break;
        case 'art':
          if (beat.artwork) /** @type {HTMLImageElement} */ (el).src = beat.artwork;
          else el.removeAttribute('src');
          break;
        case 'link':
          el.setAttribute('href', beat.url || '#');
          if (el.matches('[data-mrk-action="buy"]')) {
            el.setAttribute('aria-label', buyLabel);
            el.toggleAttribute('hidden', !beat.isBeat);
          }
          break;
      }
    }

    if ('mediaSession' in navigator) {
      navigator.mediaSession.metadata = new MediaMetadata({
        title: beat.title,
        artist: this.#strings.artist || '',
        artwork: beat.artwork ? [{ src: new URL(beat.artwork, location.href).href, sizes: '600x600' }] : [],
      });
    }
    this.#renderQueue();
  }

  #renderQueue() {
    const list = this.querySelector('[data-mrk-queue-list]');
    if (!list || this.queuePanel.hidden) return;
    const fragment = document.createDocumentFragment();
    this.queue.forEach((beat, index) => {
      const item = document.createElement('li');
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'mrk-player__queue-item';
      button.dataset.mrkQueueIndex = String(index);
      if (index === this.index) button.setAttribute('aria-current', 'true');
      const img = document.createElement('img');
      img.alt = '';
      img.width = 40;
      img.height = 40;
      img.loading = 'lazy';
      if (beat.artwork) img.src = beat.artwork;
      const text = document.createElement('span');
      text.className = 'mrk-player__queue-text';
      const title = document.createElement('span');
      title.className = 'mrk-player__queue-name';
      title.textContent = beat.title;
      const meta = document.createElement('span');
      meta.className = 'mrk-player__meta';
      meta.textContent = [beat.bpm && (this.#strings.bpm || '').replace('__mrk_bpm__', beat.bpm), beat.key]
        .filter(Boolean)
        .join(' · ');
      text.append(title, meta);
      button.append(img, text);
      item.append(button);
      fragment.append(item);
    });
    list.replaceChildren(fragment);
  }

  /** @param {boolean} [force] */
  #toggleQueue(force) {
    const open = force ?? this.queuePanel.hidden;
    if (open) (this.sheet.open ? this.sheet : this).append(this.queuePanel);
    this.queuePanel.hidden = !open;
    for (const button of this.querySelectorAll('[data-mrk-action="queue"][aria-expanded]')) {
      button.setAttribute('aria-expanded', String(open));
    }
    if (open) {
      this.#renderQueue();
      this.queuePanel.querySelector('[aria-current="true"]')?.scrollIntoView({ block: 'nearest' });
    }
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {Element} */ (event.target);
    const queueItem = target.closest('[data-mrk-queue-index]');
    if (queueItem instanceof HTMLElement) {
      this.#load(Number(queueItem.dataset.mrkQueueIndex), { autoplay: true });
      return;
    }

    if (this.#phone.matches && !this.sheet.open && target.closest('.mrk-player__track')) {
      event.preventDefault();
      this.#openSheet();
      return;
    }

    const action = /** @type {HTMLElement | null} */ (target.closest('[data-mrk-action]'))?.dataset.mrkAction;
    switch (action) {
      case 'toggle':
        return this.toggle();
      case 'prev':
        return this.prev();
      case 'next':
        return this.next();
      case 'shuffle':
      case 'repeat':
        this[action] = !this[action];
        for (const button of this.querySelectorAll(`[data-mrk-action="${action}"]`)) {
          button.setAttribute('aria-pressed', String(this[action]));
        }
        return this.#save(true);
      case 'mute':
        this.audio.muted = !this.audio.muted;
        if (!this.audio.muted && this.audio.volume === 0) this.audio.volume = 1;
        return;
      case 'queue':
        return this.#toggleQueue();
      case 'close':
        return this.close();
      case 'expand':
        return this.#openSheet();
      case 'collapse':
        return this.sheet.close();
      case 'buy':
        return this.#buy(event);
    }
  };

  /** @param {MouseEvent} event */
  #buy(event) {
    const beat = this.current;
    if (!beat) return;
    // Fall back to the product page link until the license dialog component is on the page.
    if (!document.querySelector('mrk-license-dialog')) return;
    event.preventDefault();
    if (this.sheet.open) this.sheet.close();
    this.#toggleQueue(false);
    emit(MRK_EVENTS.licenseOpen, { handle: beat.handle, productId: beat.id });
  }

  #openSheet() {
    if (this.sheet.open) return;
    this.querySelector('[data-mrk-wave-slot="sheet"]')?.append(this.wave);
    if (!this.queuePanel.hidden) this.sheet.append(this.queuePanel);
    this.sheet.showModal();
    document.documentElement.classList.add('mrk--player-sheet-open');
  }

  #onSheetClosed() {
    this.querySelector('[data-mrk-wave-slot="bar"]')?.append(this.wave);
    if (this.queuePanel.parentElement === this.sheet) this.#toggleQueue(false);
    this.sheet.style.removeProperty('transform');
    document.documentElement.classList.remove('mrk--player-sheet-open');
  }

  /** Swipe the sheet header down to close it. */
  #bindSheetDrag() {
    const handle = this.querySelector('[data-mrk-sheet-drag]');
    if (!handle) return;
    let startY = 0;
    let dy = 0;
    handle.addEventListener('pointerdown', (event) => {
      const e = /** @type {PointerEvent} */ (event);
      if (e.pointerType === 'mouse' || /** @type {Element} */ (e.target).closest('button')) return;
      startY = e.clientY;
      dy = 0;
      this.sheet.classList.add('mrk-player__sheet--dragging');
      handle.setPointerCapture(e.pointerId);
    });
    handle.addEventListener('pointermove', (event) => {
      if (!this.sheet.classList.contains('mrk-player__sheet--dragging')) return;
      dy = Math.max(0, /** @type {PointerEvent} */ (event).clientY - startY);
      this.sheet.style.transform = `translateY(${dy}px)`;
    });
    const end = () => {
      if (!this.sheet.classList.contains('mrk-player__sheet--dragging')) return;
      this.sheet.classList.remove('mrk-player__sheet--dragging');
      this.sheet.style.removeProperty('transform');
      if (dy > 100) this.sheet.close();
    };
    handle.addEventListener('pointerup', end);
    handle.addEventListener('pointercancel', end);
  }

  /** Document-level delegation for every [data-mrk-play] button (snippets/mrk-play-button.liquid). */
  #onDocumentClick = (/** @type {MouseEvent} */ event) => {
    const button = /** @type {Element} */ (event.target).closest?.('[data-mrk-play]');
    if (button instanceof HTMLElement) {
      event.preventDefault();
      if (this.current && String(this.current.id) === button.dataset.mrkBeatId) return this.toggle();
      const beat = beatFromElement(button);
      if (beat) this.play(beat, queueFromElement(button));
      return;
    }
    if (!this.queuePanel.hidden && !this.contains(/** @type {Node} */ (event.target))) this.#toggleQueue(false);
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key === 'Escape' && !this.queuePanel.hidden) {
      this.#toggleQueue(false);
      return;
    }
    if (!this.current || this.hidden || event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey) return;
    // Shortcuts are scoped to the player: the page keeps Space scrolling and arrow keys everywhere else.
    const target = event.target instanceof Element ? event.target : null;
    if (!target || !this.contains(target)) return;

    if (event.key === ' ' || event.code === 'Space') {
      if (target.closest(SPACE_OWNERS)) return;
      event.preventDefault();
      this.toggle();
    } else if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      if (target.closest(ARROW_OWNERS)) return;
      event.preventDefault();
      const forward = event.key === 'ArrowRight';
      if (event.shiftKey) forward ? this.next() : this.prev();
      else this.#seekTo(this.audio.currentTime + (forward ? SEEK_STEP : -SEEK_STEP));
    }
  };

  /**
   * Draws the waveform. Uses the beat's product field custom.waveform (window.mrkPeaks) when available, so the
   * MP3 is never downloaded just to draw it; falls back to decoding the audio otherwise.
   * @param {string} src
   * @param {number | string} [id]
   * @param {string} [wave] - the beat's custom.waveform value
   */
  async #loadWave(src, id, wave) {
    if (this.#waveSrc === src) return;
    this.#waveSrc = src;
    this.classList.remove('mrk-player--wave-ready');
    try {
      if (!this.#wavePromise) {
        const styles = getComputedStyle(this);
        const canvas = /** @type {HTMLElement} */ (this.querySelector('[data-mrk-wave-canvas]'));
        this.#wavePromise = import(this.dataset.mrkWavesurfer || './mrk-vendor-wavesurfer.js').then(({ default: WaveSurfer }) =>
          WaveSurfer.create({
            container: canvas,
            media: this.audio,
            height: 'auto',
            waveColor: styles.getPropertyValue('--mrk-waveform-unplayed').trim() || 'rgba(255,255,255,.35)',
            progressColor: styles.getPropertyValue('--mrk-accent').trim() || '#cd3231',
            cursorWidth: 0,
            barWidth: 2,
            barGap: 2,
            barRadius: 1,
            interact: false,
            normalize: true,
          })
        );
      }
      const wavesurfer = await this.#wavePromise;
      if (this.#waveSrc !== src) return;
      const stored = id != null ? await window.mrkPeaks?.get(id, undefined, wave) : null;
      if (this.#waveSrc !== src) return;
      if (stored) await wavesurfer.load(src, [stored.peaks], stored.duration);
      else await wavesurfer.load(src);
      if (this.#waveSrc === src) this.classList.add('mrk-player--wave-ready');
    } catch {
      if (this.#waveSrc === src) this.#waveSrc = '';
    }
  }

  #setupMediaSession() {
    if (!('mediaSession' in navigator)) return;
    /** @type {[MediaSessionAction, MediaSessionActionHandler][]} */
    const handlers = [
      ['play', () => this.#playAudio()],
      ['pause', () => this.audio.pause()],
      ['previoustrack', () => this.prev()],
      ['nexttrack', () => this.next()],
      ['seekto', (details) => this.#seekTo(details.seekTime ?? 0)],
      ['seekbackward', (details) => this.#seekTo(this.audio.currentTime - (details.seekOffset || SEEK_STEP))],
      ['seekforward', (details) => this.#seekTo(this.audio.currentTime + (details.seekOffset || SEEK_STEP))],
    ];
    for (const [action, handler] of handlers) {
      try {
        navigator.mediaSession.setActionHandler(action, handler);
      } catch {}
    }
  }

  /** @param {boolean} [force] */
  #save(force = false) {
    if (!force && performance.now() - this.#lastSave < SAVE_INTERVAL) return;
    this.#lastSave = performance.now();
    if (!this.current) return;
    try {
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          queue: this.queue,
          index: this.index,
          currentTime: this.#pendingTime ?? this.audio.currentTime,
          duration: this.#duration(),
          volume: this.audio.volume,
          muted: this.audio.muted,
          shuffle: this.shuffle,
          repeat: this.repeat,
        })
      );
    } catch {}
  }

  /** Restores the last session in a paused "resume" state: browsers need a user gesture before audio plays. */
  #restore() {
    /** @type {any} */
    let saved = null;
    try {
      saved = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
    } catch {}
    if (!saved || !Array.isArray(saved.queue) || !saved.queue[saved.index]) return;

    this.queue = saved.queue;
    this.shuffle = !!saved.shuffle;
    this.repeat = !!saved.repeat;
    for (const action of /** @type {const} */ (['shuffle', 'repeat'])) {
      for (const button of this.querySelectorAll(`[data-mrk-action="${action}"]`)) {
        button.setAttribute('aria-pressed', String(this[action]));
      }
    }
    if (typeof saved.volume === 'number') this.audio.volume = Math.min(Math.max(saved.volume, 0), 1);
    this.audio.muted = !!saved.muted;
    this.#resume = true;
    this.classList.add('mrk-player--resume');
    this.#load(saved.index, { time: Number(saved.currentTime) || 0 });
    this.#savedDuration = Number(saved.duration) || 0;
    this.#update(true);
  }
}

if (!customElements.get('mrk-player')) customElements.define('mrk-player', MrkPlayer);
